/**
 * A 401 from an Edge Function — or a 403 from a guard that read the caller as
 * anonymous — can mean the browser's session no longer exists server-side,
 * even though the token it holds is unexpired and correctly signed. Supabase
 * deletes `auth.sessions` whenever `auth.users` is truncated — which is what a
 * preview reseed does — so the row behind the token is gone while the token
 * itself still verifies.
 *
 * PostgREST only checks the signature, so reads and the `has_role` lookup keep
 * answering and the app stays in its signed-in (and admin) state, while every
 * function behind `requireAuth` answers 401 and every guard that reads the
 * caller as anonymous answers 403. The visible result is a working-looking UI
 * where each privileged action fails on its own.
 *
 * Asking GoTrue settles it: `getUser` resolves the token against the session
 * row, so a rejection here means the session is dead rather than that the
 * caller was denied. Clearing it locally drops the app to sign-in, which mints
 * a session the project knows.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

type SessionClient = Pick<SupabaseClient, 'auth'>;

/**
 * GoTrue answers a token it no longer knows with 401 or 403. Anything else —
 * a network failure, a timeout, a 5xx — is auth-js reporting that it could not
 * ask, and it reports those as an `error` rather than by throwing. Treating
 * those as a dead session would sign the user out over a momentary outage, so
 * only an explicit rejection counts.
 */
const isTokenRejection = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const status = (error as { status?: unknown }).status;
  return status === 401 || status === 403;
};

/**
 * Clears the stored session when the server no longer recognises it.
 *
 * Returns true when a dead session was cleared. False means there was nothing
 * to clear, the session is still live, or the check could not be made — in
 * which case the 401/403 that prompted this was an authorization decision, not
 * an expired login, and the caller should surface it as before.
 */
export async function clearSessionIfServerRejectsIt(client: SessionClient): Promise<boolean> {
  try {
    const { data } = await client.auth.getSession();
    const accessToken = data?.session?.access_token;
    if (!accessToken) return false;

    const { error } = await client.auth.getUser(accessToken);
    if (!isTokenRejection(error)) return false;

    // Another tab may have signed in while that round trip was in flight, and
    // `signOut` clears whatever session is current rather than the one checked.
    // Only clear when the rejected token is still the one in hand.
    const { data: current } = await client.auth.getSession();
    if (current?.session?.access_token !== accessToken) return false;

    // Local scope on purpose: the server-side session is already gone, and a
    // global sign-out would have to reach the same endpoint that just
    // rejected the token.
    await client.auth.signOut({ scope: 'local' });
    return true;
  } catch (error) {
    // Never throw: callers are on an error path already, and failing to clear
    // a session must not replace the original Edge Function error.
    console.warn('[staleSession] Could not check whether the session is still valid.', error);
    return false;
  }
}
